import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Linking,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { isFirstDegreeOrHigher } from '../utils/gradeAccess';
import {
  getLoggedInStudentId,
  getUserSnapshot,
  isUserRecord,
  normalizeStudentUserRecord,
} from '../utils/sessionUser';

const CHECKS_STORAGE_PREFIX = '@kungfuapp/gettingStartedChecks/';
const BLACK_BELT_DEMO_URL = 'https://youtu.be/Uf2m9iIoPvE';
const STUDENT_DEMO_URL = 'https://youtu.be/02nMV09l44k';

function openDemoVideoUrl(url) {
  Linking.openURL(url).catch(() => {});
}

function DemoVideoLink({ label, url, done, styles }) {
  return (
    <TouchableOpacity
      onPress={() => openDemoVideoUrl(url)}
      activeOpacity={0.7}
      accessibilityRole="link"
      accessibilityLabel={label}
      style={styles.demoVideoLinkHit}
      {...(Platform.OS === 'web' ? { dataSet: { gettingStartedDemoVideoLink: 'true' } } : {})}
    >
      <Text style={[styles.demoVideoLinkText, done && styles.checklistTextDone]}>{label}</Text>
    </TouchableOpacity>
  );
}

function ChecklistItem({ id, title, children, checked, onToggle, styles }) {
  return (
    <View
      style={styles.checklistItem}
      {...(Platform.OS === 'web' ? { dataSet: { gettingStartedCheckItem: 'true' } } : {})}
    >
      <TouchableOpacity
        style={styles.checklistTapRow}
        onPress={() => onToggle(id)}
        activeOpacity={0.85}
        accessibilityRole="checkbox"
        accessibilityState={{ checked }}
      >
        <View style={[styles.checkbox, checked && styles.checkboxSelected]}>
          {checked ? <Text style={styles.checkmark}>✓</Text> : null}
        </View>
        <Text style={[styles.checklistTitle, checked && styles.checklistTextDone]}>{title}</Text>
      </TouchableOpacity>
      <View
        style={[styles.checklistBody, checked && styles.checklistContentDone]}
        {...(Platform.OS === 'web'
          ? { dataSet: { gettingStartedChecklistBody: 'true' } }
          : {})}
      >
        {children}
      </View>
    </View>
  );
}

export default function GettingStartedPage({ navigation, route }) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const stopwatchRef = React.useRef(null);
  const [currentUser, setCurrentUser] = useState(route?.params?.user ?? null);
  const [checkedIds, setCheckedIds] = useState({});

  const loadUser = useCallback(async () => {
    try {
      if (route?.params?.user && isUserRecord(route.params.user)) {
        setCurrentUser(normalizeStudentUserRecord(route.params.user));
        return;
      }
      const storedId = await getLoggedInStudentId();
      const userId = route?.params?.userId ?? storedId ?? 1;
      const res = await fetch(`${getApiUrl()}/api/users/${encodeURIComponent(String(userId))}`);
      if (res.ok) {
        const data = await res.json();
        if (isUserRecord(data)) {
          setCurrentUser(normalizeStudentUserRecord(data));
          return;
        }
      }
      const snap = await getUserSnapshot();
      if (snap) setCurrentUser(snap);
    } catch {
      const snap = await getUserSnapshot();
      if (snap) setCurrentUser(snap);
    }
  }, [route?.params?.user, route?.params?.userId]);

  const loadChecks = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const raw = await AsyncStorage.getItem(`${CHECKS_STORAGE_PREFIX}${userId}`);
      if (!raw) {
        setCheckedIds({});
        return;
      }
      const parsed = JSON.parse(raw);
      setCheckedIds(parsed && typeof parsed === 'object' ? parsed : {});
    } catch {
      setCheckedIds({});
    }
  }, []);

  const saveChecks = useCallback(async (userId, next) => {
    if (!userId) return;
    try {
      await AsyncStorage.setItem(`${CHECKS_STORAGE_PREFIX}${userId}`, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void loadUser();
    }, [loadUser])
  );

  useEffect(() => {
    if (currentUser?.id) void loadChecks(currentUser.id);
  }, [currentUser?.id, loadChecks]);

  const isBlackBelt = isFirstDegreeOrHigher(currentUser);

  const visibleItemIds = useMemo(() => {
    const ids = ['profile-pictures', 'video-tutorial'];
    if (isBlackBelt) {
      ids.push('black-belt-stories', 'black-belts-training');
    }
    return ids;
  }, [isBlackBelt]);

  const toggleCheck = (id) => {
    if (!visibleItemIds.includes(id)) return;
    setCheckedIds((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      void saveChecks(currentUser?.id, next);
      return next;
    });
  };

  const goToProfile = () => {
    const userId = currentUser?.id ?? route?.params?.userId;
    navigation.navigate('Profile', { userId });
  };

  return (
    <View
      style={styles.container}
      data-screen="getting-started"
      {...(Platform.OS === 'web'
        ? { dataSet: { screen: 'getting-started' } }
        : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />

      <View style={styles.header} data-header="true">
        <View style={styles.hamburgerContainer} data-header-element="hamburger">
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer} data-header-element="breadcrumb">
          <Text style={styles.breadcrumb}>Getting Started</Text>
        </View>
        <View style={styles.profileIconContainer} data-header-element="profile">
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        {...(Platform.OS === 'web' ? { dataSet: { gettingStartedScroll: 'true' } } : {})}
      >
        <Text style={styles.intro}>
          Work through this checklist to get set up. Tap each item to mark it complete.
        </Text>

        <ChecklistItem
          id="profile-pictures"
          title="Profile Picture & Grade Picture"
          checked={!!checkedIds['profile-pictures']}
          onToggle={toggleCheck}
          styles={styles}
        >
          <View style={styles.inlineSentence}>
            <Text style={[styles.bodyText, checkedIds['profile-pictures'] && styles.checklistTextDone]}>
              Upload your profile picture and your (most recent) grade picture from your profile page{' '}
            </Text>
            <TouchableOpacity
              style={styles.hereButton}
              onPress={goToProfile}
              activeOpacity={0.8}
              accessibilityRole="button"
              accessibilityLabel="Go to profile page"
              nativeID="getting-started-here-button"
              {...(Platform.OS === 'web'
                ? { dataSet: { gettingStartedHereButton: 'true' } }
                : {})}
            >
              <Text style={styles.hereButtonText}>here</Text>
            </TouchableOpacity>
          </View>
        </ChecklistItem>

        {isBlackBelt ? (
          <ChecklistItem
            id="black-belt-stories"
            title="Black Belt stories"
            checked={!!checkedIds['black-belt-stories']}
            onToggle={toggleCheck}
            styles={styles}
          >
            <Text
              style={[styles.bodyText, checkedIds['black-belt-stories'] && styles.checklistTextDone]}
            >
              Please add 1 or more stories based on the courses. These are stories around iron fist,
              iron head, iron palm. Old stories of Sibo, previous masters, old school stories of
              students, what is capable and what has been achieved previously with this course.
            </Text>
            <Text
              style={[styles.bodyText, checkedIds['black-belt-stories'] && styles.checklistTextDone]}
            >
              Go to courses → select course, add story.
            </Text>
          </ChecklistItem>
        ) : null}

        <ChecklistItem
          id="video-tutorial"
          title="Video tutorial for app"
          checked={!!checkedIds['video-tutorial']}
          onToggle={toggleCheck}
          styles={styles}
        >
          {isBlackBelt ? (
            <>
              <DemoVideoLink
                label="Black belt platform demo video"
                url={BLACK_BELT_DEMO_URL}
                done={!!checkedIds['video-tutorial']}
                styles={styles}
              />
              <DemoVideoLink
                label="Student platform demo video"
                url={STUDENT_DEMO_URL}
                done={!!checkedIds['video-tutorial']}
                styles={styles}
              />
            </>
          ) : (
            <DemoVideoLink
              label="Platform demo video"
              url={STUDENT_DEMO_URL}
              done={!!checkedIds['video-tutorial']}
              styles={styles}
            />
          )}
        </ChecklistItem>

        {isBlackBelt ? (
          <ChecklistItem
            id="black-belts-training"
            title="Black Belts training"
            checked={!!checkedIds['black-belts-training']}
            onToggle={toggleCheck}
            styles={styles}
          >
            <Text
              style={[
                styles.bodyText,
                checkedIds['black-belts-training'] && styles.checklistTextDone,
              ]}
            >
              I&apos;ve added exercises but it would be great to start with what you regularly train
              with and start to log the exercises. The app still maintains who should see what.
            </Text>
            <Text style={styles.subheading}>Suggestion:</Text>
            <Text
              style={[
                styles.bodyText,
                checkedIds['black-belts-training'] && styles.checklistTextDone,
              ]}
            >
              Start with your courses.
            </Text>
            <Text
              style={[
                styles.bodyText,
                checkedIds['black-belts-training'] && styles.checklistTextDone,
              ]}
            >
              Go through your current grades specific exercises, then work backwards. 3rd degree black
              belts will not see 5th and 6th degree black belt exercises. Red belts will not see
              shodan ho exercises. Individual exercises will also need to be manually assigned, even
              if student is doing that course.
            </Text>
          </ChecklistItem>
        ) : null}
      </ScrollView>

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const LIGHT_CHECKLIST_PANEL = '#faf8f4';

const getStyles = (theme) => {
  const textShellBg = theme.isDarkMode ? 'transparent' : LIGHT_CHECKLIST_PANEL;

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: theme.isDarkMode ? '#000000' : '#FFFFFF',
    },
    hamburgerContainer: {
      marginRight: 15,
      backgroundColor: 'transparent',
    },
    breadcrumbContainer: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    breadcrumb: {
      fontSize: 20,
      fontWeight: '700',
      color: theme.colors.heading,
      backgroundColor: 'transparent',
    },
    profileIconContainer: {
      backgroundColor: 'transparent',
    },
    content: {
      flex: 1,
    },
    contentContainer: {
      padding: 20,
      paddingBottom: 40,
    },
    intro: {
      fontSize: 15,
      lineHeight: 22,
      color: theme.colors.text,
      marginBottom: 20,
      opacity: 0.9,
      backgroundColor: 'transparent',
    },
    checklistItem: {
      marginBottom: 18,
      padding: 14,
      borderRadius: 10,
      borderWidth: theme.isDarkMode ? 1 : 2,
      borderColor: theme.isDarkMode ? theme.colors.border : '#d4af37',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#faf8f4',
    },
    checklistTapRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 8,
      backgroundColor: textShellBg,
    },
    checkbox: {
      width: 24,
      height: 24,
      borderRadius: 4,
      borderWidth: 2,
      borderColor: theme.colors.primary || '#d4af37',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 12,
      marginTop: 2,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    checkboxSelected: {
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    checkmark: {
      color: '#FFFFFF',
      fontSize: 14,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    checklistBody: {
      marginLeft: 36,
      backgroundColor: textShellBg,
    },
    checklistTitle: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.colors.heading || theme.colors.text,
      marginBottom: 8,
      backgroundColor: textShellBg,
    },
    checklistContentDone: {
      opacity: 0.65,
    },
    checklistTextDone: {
      textDecorationLine: 'line-through',
      opacity: 0.65,
    },
    bodyText: {
      fontSize: 15,
      lineHeight: 22,
      color: theme.colors.text,
      marginBottom: 6,
      backgroundColor: textShellBg,
    },
    inlineSentence: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      backgroundColor: textShellBg,
    },
    subheading: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.colors.text,
      marginTop: 4,
      marginBottom: 4,
      backgroundColor: textShellBg,
    },
    hereButton: {
      alignSelf: 'flex-start',
      backgroundColor: '#d4af37',
      paddingVertical: 4,
      paddingHorizontal: 12,
      borderRadius: 6,
      marginVertical: 4,
    },
    hereButtonText: {
      color: '#FFFFFF',
      fontSize: 15,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    demoVideoLinkHit: {
      alignSelf: 'flex-start',
      marginBottom: 8,
      backgroundColor: textShellBg,
    },
    demoVideoLinkText: {
      fontSize: 15,
      lineHeight: 22,
      color: theme.colors.primary || '#d4af37',
      textDecorationLine: 'underline',
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
  });
};
